import React, { useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { Field, FieldArray, Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { filter, get } from 'lodash';
import { object, string, array } from 'yup';
import PropTypes from 'prop-types';
import val from 'lodash/values';
import keyBy from 'lodash/keyBy';
import { mapValues } from 'lodash/object';
import isEmpty from 'lodash/isEmpty';
import Title from '../../Title';
import EditModeCard, { EditModeCardSection } from '../../EditModeCard';
import TextField from '../../Fields/TextField';
import AddAnotherField from '../../AddAnotherField';
import AddAnotherFieldButton from '../../AddAnotherField/AddAnotherFieldButton';
import { getContactsStateForUpdate } from '../../../utils/common';
import { SaveButtonBlock } from '../SaveButtonBlock';
import { contactsIdentifiers, TP, urlRegex } from '../../../constants/consts';
import { isFocusedAddAnotherField } from '../../../utils/editCommon';

const useStyles = makeStyles(theme => ({
  formSection: {
    width: '100%',
    marginBottom: '30px',
  },
  addAnother: {
    maxWidth: `${125 / 3}%`,
    [theme.breakpoints.down('md')]: {
      maxWidth: `${200 / 3}%`,
    },
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%',
    },
  },
  errorText: {
    position: 'absolute',
    bottom: -16,
  },
}));

const formStaticFields = {
  website: {
    name: 'website',
    label: `${TP}.FN_ENTER_LINK`,
    value: '',
    contactTypeId: contactsIdentifiers.website,
    sns: null,
  },
  facebook: {
    name: 'facebook',
    label: `${TP}.FN_FACEBOOK_PAGE`,
    value: '',
    contactTypeId: contactsIdentifiers.sns,
    sns: 'facebook',
  },
  twitter: {
    name: 'twitter',
    label: `${TP}.FN_TWITTER_PAGE`,
    value: '',
    contactTypeId: contactsIdentifiers.sns,
    sns: 'twitter',
  },
  instagram: {
    name: 'instagram',
    label: `${TP}.FN_INSTAGRAM_PAGE`,
    value: '',
    contactTypeId: contactsIdentifiers.sns,
    sns: 'instagram',
  },
  youtube: {
    name: 'youtube',
    label: `${TP}.FN_YOUTUBE`,
    value: '',
    contactTypeId: contactsIdentifiers.sns,
    sns: 'youtube',
  },
  linkedin: {
    name: 'linkedin',
    label: 'Linkedin',
    value: '',
    contactTypeId: contactsIdentifiers.sns,
    sns: 'linkedin',
  },
  vk: {
    name: 'vk',
    label: `${TP}.FN_VK`,
    value: '',
    contactTypeId: contactsIdentifiers.sns,
    sns: 'vk',
  },
  vimeo: {
    name: 'vimeo',
    label: 'Vimeo',
    value: '',
    contactTypeId: contactsIdentifiers.sns,
    sns: 'vimeo',
  },
};

const { website, facebook, twitter, instagram, youtube, linkedin, vk, vimeo } = formStaticFields;
const formSocialFields = [facebook, twitter, linkedin, youtube, instagram, vk, vimeo];

const formField = (fieldName, incomingValue) => ({
  id: incomingValue?.id,
  value: incomingValue?.value || formStaticFields[fieldName]?.value, // to avoid null in textField
});

export const getDigitalPresenceContactsInitialFormValues = (
  contacts,
  fields = formStaticFields,
  fieldConstructor = formField,
) => {
  const base = {};
  const incomingContacts = keyBy(contacts, 'name');
  Object.values(fields).forEach(field => {
    base[field?.name] = fieldConstructor(field?.name, incomingContacts[field?.name]);
  });
  const other = filter(incomingContacts, field => field.contactType?.id === contactsIdentifiers.other).map(
    otherField => ({
      name: otherField?.name,
      ...fieldConstructor(otherField?.name, incomingContacts[otherField?.name]),
    }),
  );
  return { ...base, other: val(other, 'name') };
};

const DigitalPresenceForm = ({ goToNextTab, contacts, updateContacts, updatedAt, sectionTitles, isUpdating }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const formRef = useRef();

  const getInitValues = React.useCallback(() => getDigitalPresenceContactsInitialFormValues(contacts), [contacts]);

  const transformFieldForSubmit = (fieldName, fieldValues) => ({
    ...(fieldValues?.id ? { id: fieldValues?.id } : {}),
    name: fieldName,
    value: fieldValues?.value,
    contactType: {
      id: get(formStaticFields, `${fieldName}.contactTypeId`, contactsIdentifiers.other),
    },
    sns: get(formStaticFields, `${fieldName}.sns`, null),
  });

  const getSignificantContactsArray = valuesObj => {
    const valuesToSend = [];
    const definedContactsKeys = Object.keys(valuesObj);
    definedContactsKeys.forEach(
      fieldName =>
        get(valuesObj, `${fieldName}.value`) &&
        valuesToSend.push(transformFieldForSubmit(fieldName, valuesObj[fieldName])),
    );
    return valuesToSend;
  };

  const saveAndNext = formValue => {
    const { newContacts, updatedContacts, deletedContactsIds } = getContactsStateForUpdate(formValue, contacts);
    if (isEmpty(updatedContacts) && isEmpty(newContacts) && isEmpty(deletedContactsIds)) {
      goToNextTab();
    } else {
      updateContacts({
        newContacts: getSignificantContactsArray(newContacts),
        updatedContacts: getSignificantContactsArray(updatedContacts, true),
        deletedContacts: deletedContactsIds,
      });
    }
  };

  return (
    <>
      <Formik
        initialValues={getInitValues()}
        onSubmit={values => {
          saveAndNext(values);
        }}
        enableReinitialize
        validationSchema={digitalFormSchema}
        innerRef={formRef}
      >
        {({ values }) => (
          <Form>
            <Title size="big" style={{ fontWeight: 'bold' }}>
              {t(sectionTitles.websiteTitle)}
            </Title>
            <EditModeCard className={classes.formSection}>
              <EditModeCardSection first>
                <Grid container>
                  <Grid item lg={5} sm={8} xs={12}>
                    <Field
                      component={TextField}
                      name={`${website.name}.value`}
                      label={t(website.label)}
                      nativeInputProps={{ maxLength: 255 }}
                    />
                  </Grid>
                </Grid>
              </EditModeCardSection>
            </EditModeCard>
            <Title size="big" style={{ fontWeight: 'bold' }}>
              {t(sectionTitles.socialLinksTitle)}
            </Title>
            <EditModeCard className={classes.formSection}>
              <EditModeCardSection first>
                <Grid container spacing={2} direction="column">
                  {formSocialFields.map(socialField => (
                    <Grid item key={socialField.name} lg={5} sm={8} xs={12}>
                      <Field name={`${socialField.name}.value`} component={TextField} label={t(socialField.label)} />
                    </Grid>
                  ))}
                </Grid>
              </EditModeCardSection>
              <EditModeCardSection>
                <Grid container direction="column" spacing={2}>
                  <FieldArray
                    name="other"
                    render={arrayHelpers => {
                      const addFunc = index =>
                        arrayHelpers.push({
                          name: `other_${index}`,
                          value: '',
                        });
                      return values.other.length > 0 ? (
                        values.other.map((field, index) => (
                          <Grid item key={field.name}>
                            <AddAnotherField
                              values={values.other}
                              removeFunc={() => arrayHelpers.remove(index)}
                              addFunc={() => addFunc(index + 1)}
                              isLast={index === values.other.length - 1}
                              addDisabled={field.value.length === 0}
                              inputClassName={classes.addAnother}
                            >
                              <Field
                                autoFocus={isFocusedAddAnotherField(index, values.other)}
                                name={`other.${index}.value`}
                                component={TextField}
                                label={t(`${TP}.m_OTHER`)}
                                FormHelperTextProps={{ classes: { root: classes.errorText } }}
                              />
                            </AddAnotherField>
                          </Grid>
                        ))
                      ) : (
                        <Grid item>
                          <AddAnotherFieldButton onClick={() => addFunc(0)} />
                        </Grid>
                      );
                    }}
                  />
                </Grid>
              </EditModeCardSection>
            </EditModeCard>
            <SaveButtonBlock updatedDate={updatedAt} isLoading={isUpdating} />
          </Form>
        )}
      </Formik>
    </>
  );
};

DigitalPresenceForm.propTypes = {
  goToNextTab: PropTypes.func,
  contacts: PropTypes.array.isRequired,
  updateContacts: PropTypes.func,
  updatedAt: PropTypes.string,
  sectionTitles: PropTypes.shape({ websiteTitle: PropTypes.string, socialLinksTitle: PropTypes.string }),
  isUpdating: PropTypes.bool,
};

DigitalPresenceForm.defaultProps = {
  goToNextTab: () => {},
  updateContacts: () => {},
  sectionTitles: { websiteTitle: `${TP}.m_WEBSITE`, socialLinksTitle: `${TP}.FN_SOCIAL_PRESENCE` },
  isUpdating: false,
};

export default DigitalPresenceForm;

const digitalFormSchema = () =>
  object().shape({
    ...mapValues(formStaticFields, () =>
      object().shape({
        value: string()
          .matches(urlRegex, `${TP}.FN_WEB_LINK_INVALID`)
          .nullable(),
      }),
    ),
    other: array().of(
      object().shape({
        value: string()
          .matches(urlRegex, `${TP}.FN_WEB_LINK_INVALID`)
          .nullable(),
      }),
    ),
  });
